/**
 * Messaging-channel health hook.
 *
 * Follows the TanStack Query convention in `useAppVersions.js`. Polls on an
 * interval so a settings page left open reflects a channel going down without
 * anyone reloading — the whole point of the feature is that nobody was
 * watching last time.
 */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchMessagingHealth } from "services/messagingHealth";
import { WHATSAPP_INSTANCE_LABELS, formatWhatsappInstancePhone } from "constants/whatsappInstances";

/** Query key factory — keeps cache keys consistent. */
export const messagingHealthKeys = {
    all: ["messaging-health"],
};

/** Read current channel state, refreshing every 60s while the page is open. */
export const useGetMessagingHealth = (options = {}) => {
    const queryClient = useQueryClient();
    const [isProbing, setIsProbing] = useState(false);
    const { enabled = true } = options;

    const query = useQuery({
        queryKey: messagingHealthKeys.all,
        // The background poll deliberately does NOT force a probe: the server
        // answers from the health cron's last observation, so keeping a page
        // open costs nothing and still surfaces a channel dropping within a
        // few minutes.
        queryFn: () => fetchMessagingHealth(),
        enabled,
        refetchInterval: 60_000,
    });

    /**
     * What the تحديث button does — and it has to be different from `refetch`.
     *
     * A plain refetch re-asks the API, which answers from its own cache, so the
     * "آخر فحص" timestamp would not move and the button would look broken.
     * This forces a live probe of UltraMsg and the SMS gateway, then writes the
     * result into the query cache so every consumer updates at once. That is
     * the whole point of pressing it: you have just re-linked an instance and
     * want to know NOW, not within five minutes.
     */
    const refresh = async () => {
        setIsProbing(true);
        try {
            const fresh = await fetchMessagingHealth({ refresh: true });
            queryClient.setQueryData(messagingHealthKeys.all, fresh);
            return fresh;
        } finally {
            setIsProbing(false);
        }
    };

    return {
        health: query.data ?? null,
        loading: query.isLoading,
        refreshing: query.isFetching || isProbing,
        error: query.error?.message ?? null,
        refresh,
    };
};

/**
 * The two WhatsApp sending accounts, for any picker that has to name them.
 *
 * Shares the health query's cache, so the city form and the status page make
 * one request between them. The number comes from UltraMsg via the api rather
 * than a constant, which is the whole point — the hardcoded one had drifted and
 * nobody noticed until an outage made someone look.
 *
 * `phone` is null while an instance is disconnected: UltraMsg only reports the
 * linked account while it is linked, and an honest blank beats a stale number.
 */
export const useWhatsappInstances = () => {
    const query = useQuery({
        queryKey: messagingHealthKeys.all,
        queryFn: () => fetchMessagingHealth(),
    });

    const fromApi = query.data?.whatsapp ?? [];

    const instances = [1, 2].map((value) => {
        const live = fromApi.find((instance) => instance.number === value);

        return {
            value,
            label: WHATSAPP_INSTANCE_LABELS[value],
            phone: formatWhatsappInstancePhone(live?.phone),
            isConnected: Boolean(live?.isHealthy),
        };
    });

    return { instances, loading: query.isLoading };
};
